import { asyncSource, empty } from './iterators';
import { Subject } from 'rxjs';

// A list which can be iterated over. The built-in Array type satisfies
// this interface.
export interface IterableList<T> {
  readonly length: number;
  [Symbol.iterator](): IterableIterator<Readonly<T>>;
}

// An IterableList with methods for async iteration. See these methods'
// implementations for more details.
export interface AsyncIterableList<T> extends IterableList<T> {
  [Symbol.asyncIterator](): AsyncIterableIterator<Readonly<T>>;
  batches(): AsyncIterableIterator<Iterable<Readonly<T>>>;
  onAppend(cb: (seq: Iterable<Readonly<T>>) => void): void;
}

// A list to which you can append other lists. The appended list is not
// guaranteed to be copied, so callers should copy it if they intend to mutate
// it after calling append().
export interface AppendableList<T> extends AsyncIterableList<T> {
  append(list: IterableList<T>): void;
}

// A basic implementation of AppendableList<T>; behaves as if each list is
// simply concatenated together. Subclasses can implement more sophisticated
// behavior; normally all that's required is to override [Symbol.iterator].
export class AppendOnlyList<T, SubSeq extends IterableList<Readonly<T>> = IterableList<Readonly<T>>>
  implements AppendableList<T>
{
  protected _subsequences: SubSeq[] = [];
  private _cachedLength: number | undefined = undefined;
  // Allows reading items through AsyncIterable-based interfaces.
  private readonly _future: Subject<SubSeq>;

  private _closed = false;

  constructor(initializer?: SubSeq) {
    this._future = new Subject<SubSeq>();

    if (initializer) {
      this.append(initializer);
    }
  }

  /**
   * Append `sequence` to this list.
   * Throws with undefined behavior if close() has been called.
   */
  append(sequence: SubSeq): void {
    if (this._closed) {
      throw new Error('Tried to append to a closed AppendOnlyList.');
    }
    if (sequence.length === 0) {
      return;
    }
    this._subsequences.push(sequence);
    this._future.next(sequence);
    this._cachedLength = undefined;
  }

  get length(): number {
    if (this._cachedLength === undefined) {
      // Out of paranoia, since we don't know what subclass iterators may do, we
      // don't increment 'this._cachedLength' in place.
      let length = 0;
      for (const _ of this) {
        length++;
      }
      this._cachedLength = length;
    }
    return this._cachedLength;
  }

  *[Symbol.iterator](): IterableIterator<Readonly<T>> {
    for (const subsequence of this._subsequences) {
      for (const element of subsequence) {
        yield element;
      }
    }
  }

  /**
   * Close in-progress async iterators (returned by methods following
   * this one) and make future append() calls throw.
   */
  close(): void {
    this._closed = true;
    this._future.complete();
  }

  /**
   * Asynchronous iteration can be used to loop through all items in
   * this list over, without restarting.
   */
  async *[Symbol.asyncIterator](): AsyncIterableIterator<Readonly<T>> {
    for await (const batch of this.batches()) {
      yield* batch;
    }
  }

  /**
   * batches() iterates though subsequences, which
   * can be more efficient then async iteration through elements.
   */
  async *batches(): AsyncIterableIterator<SubSeq> {
    // Need to explicitly for-of loop through this._subsequences in order
    // to catch subsequences added during the course of this iteration
    // (but before the call to this.futureBatches()).
    for (const seq of this._subsequences) {
      yield seq;
    }
    yield* this.futureBatches();
  }
  /**
   * onAppend() is an event-like alternative to async iteration. Useful
   * as an interim solution for legacy code.
   */
  async onAppend(callback: (seq: Iterable<Readonly<T>>) => void): Promise<void> {
    for await (const seq of this.futureBatches()) {
      callback(seq);
    }
  }

  /**
   * futureBatches() yields yet-to-be-appended subsequences until this list is closed.
   */
  private futureBatches(): AsyncIterableIterator<SubSeq> {
    if (this._closed) {
      return empty[Symbol.asyncIterator]();
    }
    const {
      writer: { push, close },
      iterator,
    } = asyncSource<SubSeq>();
    this._future.subscribe({
      next: push,
      complete: close,
    });
    return iterator;
  }
}

export class AppendOnlyArray<T> extends AppendOnlyList<T, T[]> {
  get(idx: number): T | undefined {
    let i = idx;
    for (const sub of this._subsequences) {
      if (i < sub.length) {
        return sub[i];
      }
      i -= sub.length;
      if (i < 0) {
        return undefined;
      }
    }
    return undefined;
  }
}
